import React, { useState } from 'react';
import type { CrudAddModalProps } from '../crud/CrudTable';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { Button, Container, FileButton, Group, Loader, Stack, Text, Textarea, TextInput, Tooltip } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { modals } from '@mantine/modals';
import { useEventService } from '../../services/EventService';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { EventOccurrenceContentFragmentFragment } from '../../graphql/graphql';
import { useS3ObjectStorageService } from '@dr-pam/common-components/Services/S3ObjectStorageService';
import { IconFilePlus } from '@tabler/icons-react';
import { v4 as uuid } from 'uuid';
import z from 'zod';

export type AddEventOccurrenceContentModalProps = CrudAddModalProps<
	EventOccurrenceContentFragmentFragment,
	EventOccurrenceContentFragmentFragment
> & {
	eventOccurrenceId: string;
	sortOrder: number;
};

export const validationSchema = z.object({
	subtitle: z.string().max(500).optional().nullable(),
	description: z.string().optional().nullable(),
});

export type AddEventOccurrenceContentForm = {
	subtitle: string | null;
	description: string | null;
};

export default function AddEventOccurrenceContentModal(props: AddEventOccurrenceContentModalProps) {
	const { modalId, onCancel, onCreated, eventOccurrenceId, sortOrder } = props;

	const { addLoader, removeLoader, isLoading } = useLoadTracker();
	const eventService = useEventService();
	const s3ObjectStorageService = useS3ObjectStorageService();

	const [fileToUpload, setFileToUpload] = useState<File | null>(null);

	const form = useForm<AddEventOccurrenceContentForm>({
		initialValues: {
			subtitle: null,
			description: null,
		},
		validate: schemaResolver(validationSchema),
	});

	const handleFileSelected = (file: File | null) => {
		setFileToUpload(file);
	};

	const handleSubmit = async (values: AddEventOccurrenceContentForm) => {
		const loader = addLoader();
		try {
			let imageUrl: string | null = null;

			if (fileToUpload) {
				const imageId = uuid();
				const taggedFileName = `event-occurrence-content/${eventOccurrenceId}/${imageId}/${Date.now()}/${
					fileToUpload.name
				}`;
				await s3ObjectStorageService.uploadFile(taggedFileName, fileToUpload).response;
				imageUrl = s3ObjectStorageService.getRedirectUrl(taggedFileName);
			}

			const result = await eventService.createOccurrenceContent({
				subtitle: values.subtitle,
				description: values.description,
				imageUrl,
				sortOrder,
				eventOccurrence: { connect: { id: eventOccurrenceId } },
			});
			onCreated(result);
			NotificationUtils.showSuccess('Successfully added content block', 'Content Block');
			modals.close(modalId);
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to add content block');
			removeLoader(loader);
		}
	};

	const handleCancel = () => {
		onCancel?.();
		modals.close(modalId);
	};

	return (
		<Container>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack>
					<TextInput
						{...form.getInputProps('subtitle')}
						label="Subtitle"
						description="e.g. speaker name, lab name"
						disabled={isLoading}
					/>
					<Textarea
						{...form.getInputProps('description')}
						label="Description"
						description="e.g. speaker bio, session summary"
						rows={4}
						disabled={isLoading}
					/>
					<Group align="flex-end">
						<TextInput
							label="Image"
							value={fileToUpload?.name ?? ''}
							disabled
							style={{ flexGrow: 1 }}
							placeholder="No file selected"
						/>
						<FileButton onChange={handleFileSelected} accept="image/*">
							{(p) => (
								<Tooltip label="Click to upload image">
									<Button {...p} disabled={isLoading}>
										<IconFilePlus />
									</Button>
								</Tooltip>
							)}
						</FileButton>
						{fileToUpload && (
							<Text size="xs" c="dimmed">
								{fileToUpload.name}
							</Text>
						)}
					</Group>
					<Group justify="flex-end">
						<Button type="button" variant="subtle" onClick={handleCancel} disabled={isLoading}>
							Cancel
						</Button>
						<Button type="submit" disabled={isLoading}>
							Add content block {isLoading && <Loader size="xs" ml="xs" />}
						</Button>
					</Group>
				</Stack>
			</form>
		</Container>
	);
}
